// src/pages/system/CompanySettings.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SunatSettings from './SunatSettings';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Business as BusinessIcon,
  PhotoCamera as CameraIcon,
  Receipt as ReceiptIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  MonetizationOn as CurrencyIcon,
  Percent as PercentIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Image as ImageIcon
} from '@mui/icons-material';

function CompanySettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Estados para Logotipo
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  // Foco activo para Smart Tags
  const [focusedField, setFocusedField] = useState('pdf_footer_recibo');

  // Estados de notificación
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Valores de configuración
  const [settings, setSettings] = useState({
    company_name: '',
    company_ruc: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    tax_percentage: '18',
    currency: 'PEN',
    terms_conditions: '',
    whatsapp_template: '',
    company_logo: '',
    suspension_invoice_threshold: '1',
    pdf_footer_recibo: '',
    pdf_footer_factura: '',
    pdf_footer_boleta: '',
    series_factura: '',
    next_factura: '',
    padding_factura: '6',
    series_boleta: '',
    next_boleta: '',
    padding_boleta: '6',
    series_recibo: '',
    next_recibo: '',
    padding_recibo: '6',
    enable_whatsapp_notifications: 'true',
    enable_screen_warnings: 'true',
    email_enabled: 'false',
    email_host: '',
    email_port: '587',
    email_secure: 'false',
    email_user: '',
    email_password: '',
    email_from: ''
  });

  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/settings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          company_name: data.company_name || '',
          company_ruc: data.company_ruc || '',
          company_address: data.company_address || '',
          company_phone: data.company_phone || '',
          company_email: data.company_email || '',
          tax_percentage: data.tax_percentage || '18',
          currency: data.currency || 'PEN',
          terms_conditions: data.terms_conditions || '',
          whatsapp_template: data.whatsapp_template || '',
          company_logo: data.company_logo || '',
          suspension_invoice_threshold: data.suspension_invoice_threshold || '1',
          pdf_footer_recibo: data.pdf_footer_recibo || '',
          pdf_footer_factura: data.pdf_footer_factura || '',
          pdf_footer_boleta: data.pdf_footer_boleta || '',
          series_factura: data.series_factura || 'F001',
          next_factura: data.next_factura || '1',
          padding_factura: data.padding_factura || '6',
          series_boleta: data.series_boleta || 'B001',
          next_boleta: data.next_boleta || '1',
          padding_boleta: data.padding_boleta || '6',
          series_recibo: data.series_recibo || 'R001',
          next_recibo: data.next_recibo || '1',
          padding_recibo: data.padding_recibo || '6',
          enable_whatsapp_notifications: data.enable_whatsapp_notifications || 'true',
          enable_screen_warnings: data.enable_screen_warnings || 'true',
          email_enabled: data.email_enabled || 'false',
          email_host: data.email_host || '',
          email_port: data.email_port || '587',
          email_secure: data.email_secure || 'false',
          email_user: data.email_user || '',
          email_password: data.email_password || '',
          email_from: data.email_from || ''
        }));
        if (data.company_logo) {
          setLogoPreview(data.company_logo);
        }
      } else {
        showToast('Error al cargar las configuraciones del servidor', 'error');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showToast('Error de red al obtener configuraciones', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  // Función para redimensionar y comprimir la imagen en el cliente
  const compressAndResizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          
          // Dimensiones máximas premium para el logo
          const MAX_WIDTH = 400;
          const MAX_HEIGHT = 150;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Exportar manteniendo la transparencia del PNG o convertir a JPEG
          const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: mimeType,
                lastModified: Date.now()
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Error al exportar imagen redimensionada'));
            }
          }, mimeType, 0.85); // Calidad del 85% para JPEG
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  // Manejar selección de logo con procesamiento asíncrono
  const handleLogoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    showToast('Procesando y optimizando logotipo...', 'info');

    try {
      const optimizedFile = await compressAndResizeImage(file);
      setSelectedLogoFile(optimizedFile);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        showToast('¡Logotipo optimizado para carga rápida!', 'success');
      };
      reader.readAsDataURL(optimizedFile);
    } catch (err) {
      console.error('Error al optimizar logotipo:', err);
      if (file.size > 2 * 1024 * 1024) {
        showToast('La imagen supera el límite de 2MB', 'warning');
        return;
      }
      setSelectedLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Subir el logo al servidor
  const handleUploadLogo = async () => {
    if (!selectedLogoFile) {
      showToast('Por favor, selecciona una imagen primero', 'warning');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('logo', selectedLogoFile);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/settings/logo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, company_logo: data.logoUrl }));
        setSelectedLogoFile(null);
        showToast('¡Logotipo corporativo subido y guardado con éxito!', 'success');
        window.dispatchEvent(new Event('company-settings-updated'));
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Error al subir el logo', 'error');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      showToast('Error de conexión al subir el logotipo', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Insertar Smart Tag en la posición actual del cursor
  const insertSmartTag = (tag) => {
    if (!focusedField) return;
    const textarea = document.getElementById(focusedField);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = settings[focusedField] || '';
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newText = before + tag + after;
      
      setSettings(prev => ({ ...prev, [focusedField]: newText }));
      
      setTimeout(() => {
        textarea.focus();
        const newPos = start + tag.length;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      setSettings(prev => ({
        ...prev,
        [focusedField]: (prev[focusedField] || '') + tag
      }));
    }
  };

  // Guardar configuraciones textuales
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        showToast('Configuraciones actualizadas correctamente', 'success');
        window.dispatchEvent(new Event('company-settings-updated'));
      } else {
        showToast('Error al guardar las configuraciones de la empresa', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Error de red al intentar guardar cambios', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!settings.email_host || !settings.email_user) {
      showToast('Por favor, ingresa al menos el Servidor SMTP y el Usuario/Correo antes de probar.', 'warning');
      return;
    }

    setTestingSmtp(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/test-smtp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email_host: settings.email_host,
          email_port: settings.email_port,
          email_secure: settings.email_secure,
          email_user: settings.email_user,
          email_password: settings.email_password,
          email_from: settings.email_from,
          test_recipient: testRecipient || settings.company_email || settings.email_user
        })
      });

      const resData = await response.json();
      if (response.ok) {
        showToast(resData.message || '¡Prueba SMTP exitosa! Revisa tu bandeja de entrada.', 'success');
      } else {
        showToast(resData.message || 'Error al conectar al servidor SMTP.', 'error');
      }
    } catch (err) {
      console.error('Error enviando correo de prueba:', err);
      showToast('Error de conexión con el servidor de correo', 'error');
    } finally {
      setTestingSmtp(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
        <CircularProgress size={50} thickness={4} />
        <Typography variant="body1" color="textSecondary">
          Cargando configuración de la empresa...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, sm: 2.5, md: 3 } }}>
      {/* Encabezado */}
      <Box mb={3} display="flex" alignItems="center" gap={2}>
        <Box sx={{ bgcolor: 'primary.light', p: 1.5, borderRadius: 2, color: 'primary.main', display: 'flex' }}>
          <BusinessIcon fontSize="large" />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold">Configuración de Empresa</Typography>
          <Typography variant="body2" color="textSecondary">
            Administra los datos generales de tu WISP, servidor de correo SMTP, logotipo corporativo, códigos QR e información fiscal.
          </Typography>
        </Box>
      </Box>

      {/* Tarjeta de Control principal con Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}
        >
          <Tab icon={<BusinessIcon fontSize="small" />} iconPosition="start" label="Datos Generales" />
          <Tab icon={<ReceiptIcon fontSize="small" />} iconPosition="start" label="Facturación y Plantillas" />
          <Tab icon={<ReceiptIcon fontSize="small" />} iconPosition="start" label="Facturación SUNAT" />
          <Tab icon={<EmailIcon fontSize="small" />} iconPosition="start" label="Correo SMTP" />
        </Tabs>

        <Box p={{ xs: 2, sm: 3, md: 4 }}>
          {/* PESTAÑA 1: DATOS GENERALES */}
          {activeTab === 0 && (
            <form onSubmit={handleSaveSettings}>
              <Grid container spacing={3}>
                {/* Formulario a la izquierda (8 columnas) */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nombre Comercial / Razón Social"
                        name="company_name"
                        value={settings.company_name}
                        onChange={handleChange}
                        required
                        helperText="Nombre que verán tus clientes en facturas y portales."
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="RNC / RUC / Identificación Fiscal"
                        name="company_ruc"
                        value={settings.company_ruc}
                        onChange={handleChange}
                        required
                        helperText="Número de identificación tributaria legal."
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Teléfono Corporativo"
                        name="company_phone"
                        value={settings.company_phone}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Correo Electrónico de Contacto"
                        name="company_email"
                        type="email"
                        value={settings.company_email}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Dirección Principal / Oficina"
                        name="company_address"
                        value={settings.company_address}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                              <LocationIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Logotipo a la derecha (4 columnas) */}
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                    <Box textAlign="center" mb={1}>
                      <BusinessIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="textPrimary">Logotipo Corporativo</Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', minHeight: '36px' }}>
                        Renderizado dinámico en la cabecera superior izquierda del comprobante PDF. Alto máx 150px.
                      </Typography>
                    </Box>
                    
                    <Box sx={{
                      my: 2.5,
                      p: 1.5,
                      border: '1px dashed #cbd5e1',
                      borderRadius: 2,
                      bgcolor: 'white',
                      height: '120px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      ) : (
                        <Typography variant="caption" color="textSecondary">Sin logotipo configurado</Typography>
                      )}
                    </Box>

                    <Box>
                      <input accept="image/*" style={{ display: 'none' }} id="logo-file-input" type="file" onChange={handleLogoSelect} />
                      <label htmlFor="logo-file-input">
                        <Button variant="outlined" component="span" fullWidth size="medium" sx={{ mb: 1, textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}>
                          Seleccionar Logo
                        </Button>
                      </label>
                      {selectedLogoFile && (
                        <Button variant="contained" color="success" fullWidth size="medium" onClick={handleUploadLogo} disabled={uploadingLogo} sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }} startIcon={<UploadIcon />}>
                          {uploadingLogo ? 'Subiendo...' : 'Guardar Logo'}
                        </Button>
                      )}
                    </Box>
                  </Card>
                </Grid>
              </Grid>

              {/* Toggles de Notificaciones y Avisos */}
              <Grid item xs={12} mt={3}>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 2.5, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="textPrimary" mb={2}>Notificaciones y Avisos</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={String(settings.enable_whatsapp_notifications) === 'true'}
                            onChange={(e) => setSettings({ ...settings, enable_whatsapp_notifications: e.target.checked ? 'true' : 'false' })}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Activar notificaciones por WhatsApp (Pagos, Facturas nuevas, Avisos)
                          </Typography>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={String(settings.enable_screen_warnings) === 'true'}
                            onChange={(e) => setSettings({ ...settings, enable_screen_warnings: e.target.checked ? 'true' : 'false' })}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Activar avisos en pantalla preventivos (Aviso de Vencimiento)
                          </Typography>
                        }
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  sx={{ px: 4, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                >
                  {saving ? 'Guardando...' : 'Guardar Información'}
                </Button>
              </Box>
            </form>
          )}

          {/* PESTAÑA 2: FACTURACIÓN Y PLANTILLAS */}
          {activeTab === 1 && (
            <form onSubmit={handleSaveSettings}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Moneda del Sistema"
                    name="currency"
                    value={settings.currency}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="PEN">PEN - Sol Peruano (S/)</MenuItem>
                    <MenuItem value="DOP">DOP - Peso Dominicano (RD$)</MenuItem>
                    <MenuItem value="USD">USD - Dólar Estadounidense ($)</MenuItem>
                    <MenuItem value="MXN">MXN - Peso Mexicano ($)</MenuItem>
                    <MenuItem value="COP">COP - Peso Colombiano ($)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Porcentaje de Impuesto (ITBIS / IGV / IVA)"
                    name="tax_percentage"
                    type="number"
                    value={settings.tax_percentage}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PercentIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Facturas Impagas Antes de Suspender Servicio"
                    name="suspension_invoice_threshold"
                    value={settings.suspension_invoice_threshold}
                    onChange={handleChange}
                    required
                    helperText="Cantidad de facturas impagas que un cliente puede acumular antes de la suspensión."
                  >
                    <MenuItem value="1">1 Factura (Suspensión al primer vencimiento)</MenuItem>
                    <MenuItem value="2">2 Facturas</MenuItem>
                    <MenuItem value="3">3 Facturas</MenuItem>
                    <MenuItem value="4">4 Facturas</MenuItem>
                    <MenuItem value="5">5 Facturas</MenuItem>
                    <MenuItem value="6">6 Facturas</MenuItem>
                    <MenuItem value="0">Nunca suspender servicio (Nunca cortar)</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="textPrimary">
                    Correlativos y Series de Comprobantes 🔢
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    Configura la serie, el siguiente número y el formato de los comprobantes generados para Facturas, Boletas y Recibos.
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Facturas */}
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary.main" mb={2}>
                          Facturas
                        </Typography>
                        <TextField
                          fullWidth
                          label="Serie"
                          name="series_factura"
                          value={settings.series_factura || ''}
                          onChange={handleChange}
                          placeholder="Ej. F001"
                          margin="dense"
                          sx={{ mb: 1.5 }}
                        />
                        <TextField
                          fullWidth
                          label="Siguiente Correlativo"
                          name="next_factura"
                          type="number"
                          value={settings.next_factura || ''}
                          onChange={handleChange}
                          placeholder="Ej. 1"
                          margin="dense"
                          sx={{ mb: 1.5 }}
                        />
                        <TextField
                          fullWidth
                          label="Largo del Número (Padding)"
                          name="padding_factura"
                          type="number"
                          value={settings.padding_factura || ''}
                          onChange={handleChange}
                          placeholder="Ej. 6"
                          margin="dense"
                          helperText="Cantidad de dígitos (ej. 6 para 000001)."
                        />
                      </Paper>
                    </Grid>
                    {/* Boletas */}
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary.main" mb={2}>
                          Boletas
                        </Typography>
                        <TextField
                          fullWidth
                          label="Serie"
                          name="series_boleta"
                          value={settings.series_boleta || ''}
                          onChange={handleChange}
                          placeholder="Ej. B001"
                          margin="dense"
                          sx={{ mb: 1.5 }}
                        />
                        <TextField
                          fullWidth
                          label="Siguiente Correlativo"
                          name="next_boleta"
                          type="number"
                          value={settings.next_boleta || ''}
                          onChange={handleChange}
                          placeholder="Ej. 1"
                          margin="dense"
                          sx={{ mb: 1.5 }}
                        />
                        <TextField
                          fullWidth
                          label="Largo del Número (Padding)"
                          name="padding_boleta"
                          type="number"
                          value={settings.padding_boleta || ''}
                          onChange={handleChange}
                          placeholder="Ej. 6"
                          margin="dense"
                          helperText="Cantidad de dígitos (ej. 6 para 000001)."
                        />
                      </Paper>
                    </Grid>
                    {/* Recibos */}
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary.main" mb={2}>
                          Recibos
                        </Typography>
                        <TextField
                          fullWidth
                          label="Serie"
                          name="series_recibo"
                          value={settings.series_recibo || ''}
                          onChange={handleChange}
                          placeholder="Ej. R001"
                          margin="dense"
                          sx={{ mb: 1.5 }}
                        />
                        <TextField
                          fullWidth
                          label="Siguiente Correlativo"
                          name="next_recibo"
                          type="number"
                          value={settings.next_recibo || ''}
                          onChange={handleChange}
                          placeholder="Ej. 1"
                          margin="dense"
                          sx={{ mb: 1.5 }}
                        />
                        <TextField
                          fullWidth
                          label="Largo del Número (Padding)"
                          name="padding_recibo"
                          type="number"
                          value={settings.padding_recibo || ''}
                          onChange={handleChange}
                          placeholder="Ej. 6"
                          margin="dense"
                          helperText="Cantidad de dígitos (ej. 6 para 000001)."
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Términos y Condiciones en Facturas PDF"
                    name="terms_conditions"
                    value={settings.terms_conditions}
                    onChange={handleChange}
                    helperText="Esta leyenda aparecerá en la parte inferior de los comprobantes emitidos."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Plantilla para Mensajes de Cobro por WhatsApp"
                    name="whatsapp_template"
                    value={settings.whatsapp_template}
                    onChange={handleChange}
                    helperText="Usa etiquetas dinámicas: {client_name}, {invoice_number}, {amount}, {due_date}."
                  />
                </Grid>

                {/* NUEVO DISEÑO COMPLETO DE LEYENDAS Y SMART TAGS */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="textPrimary">
                    Plantillas de Leyendas Fiscales en PDF 📄
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    Personaliza las leyendas y textos informativos de validez fiscal que aparecen al pie de página de cada tipo de comprobante generado por el sistema.
                  </Typography>

                  <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} sx={{ p: 2, borderRadius: 3, mb: 3, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">¿Quieres personalizar tipografías, colores y layouts de forma visual?</Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, color: '#1e3a8a' }}>
                        Diseña tu comprobante A4 con un simulador interactivo en tiempo real y descarga PDFs reales de previsualización.
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="medium" 
                      onClick={() => navigate('/system/template-editor')}
                      sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, flexShrink: 0 }}
                    >
                      Abrir Diseñador Visual A4 🎨
                    </Button>
                  </Box>

                  <Box mb={3} sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Etiquetas Inteligentes (Smart Tags) 💡
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" mb={1.5}>
                      Selecciona y haz clic en cualquiera de las siguientes etiquetas dinámicas para insertarla en la posición del cursor de la plantilla que estés editando actualmente.
                    </Typography>
                    
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {[
                        { label: 'Empresa', tag: '{company_name}' },
                        { label: 'RUC Empresa', tag: '{company_ruc}' },
                        { label: 'Cliente', tag: '{client_name}' },
                        { label: 'RUC/DNI Cliente', tag: '{client_document}' },
                        { label: 'N° Comprobante', tag: '{invoice_number}' },
                        { label: 'Total', tag: '{amount}' },
                        { label: 'Emisión', tag: '{emission_date}' },
                        { label: 'Vencimiento', tag: '{due_date}' }
                      ].map(item => (
                        <Button
                          key={item.tag}
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={() => insertSmartTag(item.tag)}
                          sx={{ 
                            textTransform: 'none', 
                            borderRadius: '16px', 
                            fontSize: '0.75rem', 
                            py: 0.5, 
                            px: 1.5,
                            borderColor: 'secondary.light',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: 'secondary.light',
                              color: 'secondary.dark'
                            }
                          }}
                        >
                          {item.label} ({item.tag})
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        id="pdf_footer_recibo"
                        fullWidth
                        multiline
                        rows={3}
                        label="Plantilla Leyenda para Recibos (Documento Interno)"
                        name="pdf_footer_recibo"
                        value={settings.pdf_footer_recibo}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('pdf_footer_recibo')}
                        helperText="Esta leyenda e instrucciones aparecerán exclusivamente al pie de los Recibos de Servicio Internos."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="pdf_footer_factura"
                        fullWidth
                        multiline
                        rows={3}
                        label="Plantilla Leyenda para Facturas Electrónicas"
                        name="pdf_footer_factura"
                        value={settings.pdf_footer_factura}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('pdf_footer_factura')}
                        helperText="Texto legal y tributario oficial que se imprimirá al pie de página de todas las Facturas Electrónicas."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="pdf_footer_boleta"
                        fullWidth
                        multiline
                        rows={3}
                        label="Plantilla Leyenda para Boletas de Venta Electrónicas"
                        name="pdf_footer_boleta"
                        value={settings.pdf_footer_boleta}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('pdf_footer_boleta')}
                        helperText="Texto legal y tributario oficial que se imprimirá al pie de página de todas las Boletas de Venta Electrónicas."
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  sx={{ px: 4, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                >
                  {saving ? 'Guardando...' : 'Guardar Configuraciones'}
                </Button>
              </Box>
            </form>
          )}

          {activeTab === 2 && (
            <SunatSettings />
          )}

          {/* PESTAÑA 4: SERVIDOR SMTP Y CORREO */}
          {activeTab === 3 && (
            <form onSubmit={handleSaveSettings}>
              <Box mb={3}>
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold">🌐 Configuración del Servidor de Correo (SMTP)</Typography>
                  <Typography variant="body2">
                    Configura tu servidor SMTP (Gmail, Outlook, cPanel o servidor privado) para enviar correos automáticos de <strong>Recuperación de Contraseñas (¿Olvidó su contraseña?)</strong>, emisión de facturas y avisos de cobro.
                  </Typography>
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.email_enabled === 'true'}
                            onChange={(e) => setSettings(prev => ({ ...prev, email_enabled: e.target.checked ? 'true' : 'false' }))}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">Activar Envío de Correos Automáticos (SMTP)</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {settings.email_enabled === 'true'
                                ? 'Habilitado: El sistema enviará correos reales usando tu servidor SMTP.'
                                : 'Deshabilitado: Los correos se simularán únicamente en la consola del servidor.'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Servidor de Correo Saliente (SMTP Host)"
                      name="email_host"
                      value={settings.email_host}
                      onChange={handleChange}
                      placeholder="ej. smtp.gmail.com o mail.tudominio.com"
                      helperText="Dirección host del servidor SMTP de tu proveedor."
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Puerto SMTP"
                      name="email_port"
                      value={settings.email_port}
                      onChange={(e) => {
                        const newPort = e.target.value;
                        setSettings(prev => ({
                          ...prev,
                          email_port: newPort,
                          email_secure: newPort === '465' ? 'true' : (newPort === '587' ? 'false' : prev.email_secure)
                        }));
                      }}
                      placeholder="587"
                      helperText="587 (STARTTLS) ó 465 (SSL)"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{ p: 1.5, px: 2, borderRadius: 2, height: '56px', display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.email_secure === 'true'}
                            onChange={(e) => setSettings(prev => ({ ...prev, email_secure: e.target.checked ? 'true' : 'false' }))}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">Conexión SSL Directa</Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {settings.email_port === '587' ? 'Desactivado para 587 (STARTTLS)' : (settings.email_secure === 'true' ? 'SSL para puerto 465' : 'STARTTLS para puerto 587')}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Usuario SMTP / Correo de Autenticación"
                      name="email_user"
                      type="email"
                      value={settings.email_user}
                      onChange={handleChange}
                      placeholder="ej. notificaciones@tudominio.com"
                      helperText="Correo o usuario de conexión en el servidor SMTP."
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contraseña SMTP / App Password"
                      name="email_password"
                      type="password"
                      value={settings.email_password}
                      onChange={handleChange}
                      placeholder="••••••••••••"
                      helperText="Contraseña de la cuenta o Contraseña de Aplicación."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nombre y Correo Remitente (Header From)"
                      name="email_from"
                      value={settings.email_from}
                      onChange={handleChange}
                      placeholder='ej. YinoWISP Soporte <soporte@tudominio.com>'
                      helperText="Formato recomendado: Nombre <correo@tudominio.com>"
                    />
                  </Grid>

                  {/* Sección de Prueba de Conexión */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px dashed #cbd5e1', bgcolor: '#f1f5f9', mt: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        🧪 Probar Envío de Correo SMTP en Vivo
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Enviar Correo de Prueba a:"
                            value={testRecipient}
                            onChange={(e) => setTestRecipient(e.target.value)}
                            placeholder={settings.company_email || settings.email_user || 'tu-correo@gmail.com'}
                            helperText="Ingresa el correo destino donde recibirás la notificación de prueba."
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            onClick={handleTestSmtp}
                            disabled={testingSmtp}
                            startIcon={testingSmtp ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
                            sx={{ py: 1, fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}
                          >
                            {testingSmtp ? 'Probando...' : 'Enviar Prueba SMTP'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{ px: 4, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {saving ? 'Guardando...' : 'Guardar Configuración SMTP'}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Box>
      </Paper>

      {/* Notificación Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CompanySettings;
